import React from 'react'
import { createPortal } from 'react-dom'
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Badge,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  Tooltip,
  Collapse,
} from '@mui/material'
import TrapFocus from '@mui/material/Unstable_TrapFocus'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MenuIcon from '@mui/icons-material/Menu'
import InboxIcon from '@mui/icons-material/InboxRounded'
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsRounded'
import InsightsIcon from '@mui/icons-material/InsightsRounded'
import DashboardIcon from '@mui/icons-material/SpaceDashboardRounded'
import SettingsIcon from '@mui/icons-material/SettingsRounded'
import ArticleIcon from '@mui/icons-material/ArticleRounded'
import SecurityIcon from '@mui/icons-material/SecurityRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import BuildIcon from '@mui/icons-material/BuildRounded'
import PushPinIcon from '@mui/icons-material/PushPin'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import HistoryIcon from '@mui/icons-material/HistoryEduRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { logout as logoutMe, type MeResponse } from '../api/me'
import { getBootstrap } from '../api'
import useUIStore from '../store/ui'
import useUiNavStore from '../state/uiNavStore'
import useMessagesStore from '../store/messages'
import useUnreadStore from '../store/unread'
import StationSwitcher from '../components/StationSwitcher'
import ListenerCountButton from '../components/header/ListenerCountButton'
import MailUnreadButton from '../components/header/MailUnreadButton'
import WeatherButton from '../components/header/WeatherButton'
import HeaderClock from '../components/header/HeaderClock'
import { keyframes } from '@mui/system'
import { PageTitleProvider, type PageTitleAlign, type PageTitleOptions } from '../components/common/PageTitleContext'
import { ErrorBoundary } from './ErrorBoundary'
import { useCssSizeVar } from '../hooks/useCssSizeVar'
import {
  SidebarProvider,
  useSidebarState,
  findParentRootId,
  getFirstChildPath,
  getFirstNavigableNode,
  type NavNode,
} from './SidebarNav'
import useStationStore, { type ActiveStation } from '../store/station'

const drawerWidth = 280
const collapsedDrawerWidth = 85
const navTransitionDuration = 200

type NavMatch = string | { path: string; end?: boolean }

type NavItem = NavNode & {
  icon?: React.ReactElement
  match?: NavMatch | NavMatch[]
  children?: NavItem[]
}

interface SidebarNavContentProps {
  items: NavItem[]
  navSections: Record<string, boolean>
  setNavSection: (section: string, expanded: boolean) => void
  toggleNavSection: (section: string) => void
  isNavItemActive: (item: NavItem) => boolean
  handleNavigate: (path: string) => void
  unreadTotal: number
  dangerBadgeStyle: React.CSSProperties
}

const SidebarNavContent: React.FC<SidebarNavContentProps> = ({
  items,
  navSections,
  setNavSection,
  toggleNavSection,
  isNavItemActive,
  handleNavigate,
  unreadTotal,
  dangerBadgeStyle,
}) => {
  const { expandedRootId, setExpandedRootId } = useSidebarState()
  const location = useLocation()
  const theme = useTheme()

  React.useEffect(() => {
    const parentId = findParentRootId(items, location.pathname)
    setExpandedRootId(parentId)
  }, [items, location.pathname, setExpandedRootId])

  const renderNavItems = (nodes: NavItem[], depth = 0): React.ReactNode =>
    nodes.map((item) => {
      const hasChildren = Boolean(item.children?.length)
      const descendantActive = hasChildren
        ? item.children?.some((child) => isNavItemActive(child)) ?? false
        : false
      const parentActive = isNavItemActive(item)
      const isRoot = depth === 0 && hasChildren
      const expanded = hasChildren
        ? isRoot
          ? expandedRootId === item.id || (!expandedRootId && descendantActive)
          : navSections[item.id] !== false || descendantActive
        : false

      const childIndent = depth === 0 ? 0 : 5 + (depth - 1) * 2

      const handleRootLabelClick = () => {
        if (!hasChildren) {
          return
        }
        const firstChild = getFirstNavigableNode(item)
        const firstChildPath = firstChild?.path
        if (firstChild && firstChildPath) {
          setExpandedRootId(item.id)
          handleNavigate(firstChildPath)
        }
      }

      const handleItemClick = () => {
        if (hasChildren && isRoot) {
          handleRootLabelClick()
          return
        }
        if (item.path) {
          handleNavigate(item.path)
        }
      }

      const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleItemClick()
          return
        }

        if (!hasChildren) {
          return
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault()
          if (isRoot) {
            setExpandedRootId(item.id)
          } else {
            setNavSection(item.id, true)
          }
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          if (isRoot) {
            if (expandedRootId === item.id) {
              setExpandedRootId(null)
            }
          } else {
            setNavSection(item.id, false)
          }
        }
      }

      return (
        <Box key={item.id} component="nav" aria-label={item.label}>
          <ListItemButton
            role="treeitem"
            selected={parentActive}
            aria-current={parentActive ? 'page' : undefined}
            onClick={handleItemClick}
            onKeyDown={handleKeyDown}
            aria-expanded={hasChildren ? expanded : undefined}
            aria-haspopup={hasChildren ? (isRoot ? 'tree' : 'true') : undefined}
            sx={{
              ...(depth === 0
                ? {
                    mx: 0.5,
                    my: 0.25,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    minHeight: 44,
                  }
                : {
                    ml: childIndent,
                    mr: 1,
                    my: 0.25,
                    px: 1.5,
                    py: 0.625,
                    borderRadius: 2,
                    minHeight: 40,
                  }),
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              },
            }}
          >
            {item.icon ? (
              <ListItemIcon
                sx={{
                  minWidth: depth === 0 ? 36 : 28,
                  color: 'inherit',
                  ...(depth > 0 ? { mr: 1 } : {}),
                }}
              >
                {item.id === 'inbox' && depth === 0 ? (
                  <Badge
                    badgeContent={unreadTotal > 0 ? unreadTotal : null}
                    overlap="circular"
                    componentsProps={{
                      badge: {
                        className: 'BadgePill BadgePill--danger',
                        style: dangerBadgeStyle,
                      },
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
            ) : null}
            <ListItemText
              primaryTypographyProps={depth > 0 ? { variant: 'body2' } : undefined}
              primary={item.label}
            />
            {hasChildren && (
              <IconButton
                size="small"
                edge="end"
                sx={{ ml: 0.25 }}
                onClick={(event) => {
                  event.stopPropagation()
                  if (isRoot) {
                    setExpandedRootId(expanded ? null : item.id)
                  } else {
                    toggleNavSection(item.id)
                  }
                }}
                aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
              >
                {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </IconButton>
            )}
          </ListItemButton>
          {hasChildren && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ py: 0.25 }}>
                {renderNavItems(item.children ?? [], depth + 1)}
              </List>
            </Collapse>
          )}
        </Box>
      )
    })

  return <List sx={{ py: 1, px: 1 }}>{renderNavItems(items)}</List>
}

const breadcrumbLabels: Record<string, string> = {
  inbox: 'Inbox',
  contacts: 'Contacts',
  rounds: 'Rounds',
  competitions: 'Competitions',
  surveys: 'Surveys',
  dashboard: 'Dashboard',
  settings: 'Settings',
  profile: 'Profile',
  integrations: 'Integrations',
  email: 'Email',
  telegram: 'Telegram',
  users: 'Users',
  prep: 'Prep Sheet',
  'show-clock': 'Show Clock',
  templates: 'Templates',
  days: 'Daily clocks',
  live: 'Live',
  superadmin: 'Superadmin',
  'stations-users': 'Stations & Users',
  weather: 'Weather',
  subscriptions: 'Subscriptions',
  products: 'Products',
  categories: 'Categories',
  'ingest-keys': 'Ingest keys',
  general: 'General',
  ytmp3: 'YT-MP3 Downloader',
  billing: 'Billing',
  'youtube-credits': 'YouTube credits',
  grants: 'Manual grants',
  consolidation: 'Consolidation',
  tools: 'Tools',
  'youtube-mp3': 'YouTube MP3 Downloader',
}

export interface AppShellProps {
  me: MeResponse
  includeSuperadmin: boolean
  onUpdateMe?: (updater: (prev: MeResponse) => MeResponse) => void
}

export type AppOutletContext = {
  me: MeResponse
  includeSuperadmin: boolean
  activeStation: ActiveStation | null
  setActiveStation: (station: ActiveStation) => void
  updateMe?: (updater: (prev: MeResponse) => MeResponse) => void
}

const livePulse = keyframes`
  0% { transform: scale(0.9); opacity: 0.6; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.9); opacity: 0.6; }
`

const LiveConnectionIndicator: React.FC<{ connected: boolean; lightweight?: boolean }> = ({
  connected,
  lightweight = false,
}) => {
  const label = lightweight
    ? 'Lightweight updates check every 30 seconds'
    : connected
      ? 'Live updates connected'
      : 'Reconnecting to live updates…'

  return (
  <Tooltip title={label}>
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 0.75,
        py: 0.5,
        borderRadius: 999,
        backgroundColor: (theme) =>
          lightweight ? theme.palette.info.main : connected ? theme.palette.error.main : theme.palette.action.selected,
        color: (theme) =>
          lightweight ? theme.palette.info.contrastText : connected ? theme.palette.error.contrastText : theme.palette.text.secondary,
        flexShrink: 0,
      }}
      aria-label={label}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#fff',
          opacity: lightweight || connected ? 1 : 0.6,
          animation: !lightweight && connected ? `${livePulse} 1.4s ease-in-out infinite` : 'none',
        }}
      />
    </Box>
  </Tooltip>
  )
}

const AppShell: React.FC<AppShellProps> = ({ me, includeSuperadmin, onUpdateMe }) => {
  const { user, stations } = me
  const stationList = stations ?? []
  const theme = useTheme()
  const loginUrl = React.useMemo(() => {
    try {
      return getBootstrap().loginUrl || '/rpa-login'
    } catch {
      return '/rpa-login'
    }
  }, [])
  const logoUrl = React.useMemo(() => {
    try {
      const { pluginUrl } = getBootstrap()
      return `${pluginUrl}public/rpa-logo.png`
    } catch {
      return '/wp-content/plugins/radio-presenter-assistant/public/rpa-logo.png'
    }
  }, [])
  const activeStation = useStationStore((state) => state.activeStation)
  const setActiveStationState = useStationStore((state) => state.setActiveStation)
  const defaultTimezone = React.useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      return 'UTC'
    }
  }, [])
  const setStation = React.useCallback(
    (station: ActiveStation) => {
      setActiveStationState(station)
    },
    [setActiveStationState],
  )

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('station=')) {
      const url = new URL(window.location.href)
      url.searchParams.delete('station')
      const nextUrl = `${url.pathname}${url.search}${url.hash}`
      window.history.replaceState({}, '', nextUrl)
    }
  }, [])

  React.useEffect(() => {
    if (!stationList.length) {
      if (activeStation) {
        setActiveStationState(null)
      }
      return
    }

    const findStationById = (id: number | null) =>
      stationList.find((station) => station.id === id) ?? null

    const preferred =
      findStationById(activeStation?.id ?? null) ??
      stationList[0] ??
      null

    if (!preferred) {
      return
    }

    const timezone = preferred.timezone || defaultTimezone

    if (
      !activeStation ||
      activeStation.id !== preferred.id ||
      activeStation.name !== preferred.name ||
      activeStation.timezone !== timezone
    ) {
      setActiveStationState({
        id: preferred.id,
        name: preferred.name,
        timezone,
      })
    }

  }, [
    activeStation,
    defaultTimezone,
    setActiveStationState,
    stationList,
  ])

  const handleStationSelect = React.useCallback(
    (station: (typeof stationList)[number]) => {
      if (!station) {
        return
      }

      const timezone = station.timezone || defaultTimezone
      setStation({ id: station.id, name: station.name, timezone })
    },
    [defaultTimezone, setStation],
  )

  const activeStationId = activeStation?.id ?? stationList[0]?.id
  const fallbackStation = stationList[0] ?? null
  const weatherStationName = activeStation?.name ?? fallbackStation?.name ?? null
  const weatherStationTimezone = activeStation?.timezone ?? fallbackStation?.timezone ?? null
  const dangerBadgeStyle = React.useMemo<React.CSSProperties>(
    () => ({
      '--badge-danger-bg': theme.palette.error.main,
      '--badge-danger-fg': '#ffffff',
    }),
    [theme.palette.error.main],
  )
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const location = useLocation()
  const navigate = useNavigate()
  const navPinned = useUiNavStore((state) => state.pinned)
  const navOpen = useUiNavStore((state) => state.open)
  const navAnimating = useUiNavStore((state) => state.animating)
  const openNav = useUiNavStore((state) => state.openNav)
  const closeNav = useUiNavStore((state) => state.closeNav)
  const toggleNav = useUiNavStore((state) => state.toggleNav)
  const toggleNavPinned = useUiNavStore((state) => state.togglePinned)
  const setNavAnimating = useUiNavStore((state) => state.setAnimating)
  const navSections = useUIStore((state) => state.navSections)
  const toggleNavSection = useUIStore((state) => state.toggleNavSection)
  const setNavSection = useUIStore((state) => state.setNavSection)
  const desktopDrawerWidth = navPinned ? drawerWidth : collapsedDrawerWidth
  const navDrawerVisible = navOpen || navAnimating
  const shouldShowOverlay = (!isDesktop || !navPinned) && navDrawerVisible
  const portalContainer = React.useMemo<HTMLElement | null>(() => {
    if (typeof window === 'undefined') {
      return null
    }
    const existing = document.getElementById('rpa-portal-root')
    if (existing) {
      return existing
    }
    const node = document.createElement('div')
    node.setAttribute('id', 'rpa-portal-root')
    document.body.appendChild(node)
    return node
  }, [])
  const hamburgerRef = React.useRef<HTMLButtonElement | null>(null)
  const headerRef = React.useRef<HTMLDivElement | null>(null)
  const drawerRef = React.useRef<HTMLDivElement | null>(null)
  const unreadTotal = useUnreadStore((state) => state.total)
  const connected = useMessagesStore((state) => state.connected)
  const [pageTitle, setPageTitleState] = React.useState('')
  const [pageTitleAlign, setPageTitleAlign] = React.useState<PageTitleAlign>('left')
  const [pageTitleBreadcrumbs, setPageTitleBreadcrumbs] = React.useState(true)
  const [pageTitleOffsetAppBar, setPageTitleOffsetAppBar] = React.useState(true)
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false)
  const [logoutPending, setLogoutPending] = React.useState(false)
  const [logoutError, setLogoutError] = React.useState<string | null>(null)
  const isPrepRoute = location.pathname.startsWith('/prep')
  const showBreadcrumbs = !isPrepRoute && pageTitleBreadcrumbs
  useCssSizeVar(headerRef, '--rpa-header-h', { defaultValue: 64 })


  const handleSetPageTitle = React.useCallback((title: string, options?: PageTitleOptions) => {
    setPageTitleState(title)
    setPageTitleAlign(options?.align ?? 'left')
    setPageTitleBreadcrumbs(options?.showBreadcrumbs ?? true)
    setPageTitleOffsetAppBar(options?.offsetAppBar ?? true)
  }, [])

  const handleClearPageTitle = React.useCallback(() => {
    setPageTitleState('')
    setPageTitleAlign('left')
    setPageTitleBreadcrumbs(true)
    setPageTitleOffsetAppBar(true)
  }, [])

  const handleLogoutClick = React.useCallback(() => {
    setLogoutError(null)
    setLogoutDialogOpen(true)
  }, [])

  const handleLogoutClose = React.useCallback(() => {
    if (logoutPending) {
      return
    }
    setLogoutDialogOpen(false)
  }, [logoutPending])

  const handleLogoutConfirm = React.useCallback(async () => {
    setLogoutPending(true)
    setLogoutError(null)
    try {
      await logoutMe()
      window.location.assign(loginUrl)
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : 'Unable to sign out.'
      setLogoutError(message)
      setLogoutPending(false)
    }
  }, [loginUrl])

  const pageTitleContextValue = React.useMemo(
    () => ({
      title: pageTitle,
      align: pageTitleAlign,
      showBreadcrumbs,
      offsetAppBar: pageTitleOffsetAppBar,
      setPageTitle: handleSetPageTitle,
      clearPageTitle: handleClearPageTitle,
    }),
    [
      pageTitle,
      pageTitleAlign,
      showBreadcrumbs,
      pageTitleOffsetAppBar,
      handleSetPageTitle,
      handleClearPageTitle,
    ],
  )

  const previousNavOpen = React.useRef(navOpen)

  React.useEffect(() => {
    if (shouldShowOverlay && navOpen) {
      const node = drawerRef.current
      if (node) {
        window.requestAnimationFrame(() => {
          node.focus()
        })
      }
    }
  }, [shouldShowOverlay, navOpen])

  React.useEffect(() => {
    if (!shouldShowOverlay || !navOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeNav()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [shouldShowOverlay, navOpen, closeNav])

  React.useEffect(() => {
    if (previousNavOpen.current && !navOpen && (!isDesktop || !navPinned)) {
      hamburgerRef.current?.focus()
    }
    previousNavOpen.current = navOpen
  }, [navOpen, isDesktop, navPinned])

  React.useEffect(() => {
    if (navPinned && navOpen && isDesktop) {
      closeNav()
      setNavAnimating(false)
    }
  }, [navPinned, navOpen, isDesktop, closeNav, setNavAnimating])


  const showYoutubeMp3 = Boolean(me.tools?.youtube_mp3_downloader)

  const navItems = React.useMemo<NavItem[]>(() => {
    const baseItems: NavItem[] = [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
      {
        id: 'inbox',
        label: 'Inbox',
        path: '/inbox',
        icon: <InboxIcon />,
        match: [
          { path: '/inbox', end: true },
          { path: '/inbox/*', end: false },
        ],
        children: [
          {
            id: 'inbox-all',
            label: 'All messages',
            path: '/inbox',
            match: [
              { path: '/inbox', end: true },
              { path: '/inbox/thread/:threadId', end: true },
              { path: '/inbox/thread/:threadId/*', end: false },
            ],
          },
          {
            id: 'inbox-contacts',
            label: 'Contacts',
            path: '/inbox/contacts',
            match: [
              { path: '/inbox/contacts', end: true },
              { path: '/inbox/contacts/:contactId', end: true },
              { path: '/inbox/contacts/:contactId/*', end: false },
            ],
          },
          {
            id: 'inbox-auto-responder',
            label: 'Auto Responder',
            path: '/inbox/auto-responder',
            match: [{ path: '/inbox/auto-responder', end: true }],
          },
          {
            id: 'competitions',
            label: 'Competitions',
            path: '/competitions',
            icon: <EmojiEventsIcon />,
            match: [
              { path: '/competitions', end: true },
              { path: '/competitions/*', end: false },
              { path: '/rounds', end: true },
              { path: '/rounds/*', end: false },
            ],
            children: [
              {
                id: 'rounds',
                label: 'Rounds',
                path: '/rounds',
                match: [
                  { path: '/rounds', end: true },
                  { path: '/rounds/*', end: false },
                ],
              },
            ],
          },
          { id: 'surveys', label: 'Surveys', path: '/surveys', icon: <InsightsIcon /> },
        ],
      },
      { id: 'prep', label: 'Prep Sheet', path: '/prep', icon: <ArticleIcon /> },
      {
        id: 'show-clock',
        label: 'Show Clock',
        path: '/show-clock/live',
        icon: <AccessTimeIcon />,
        match: [{ path: '/show-clock', end: false }, { path: '/show-clock/*', end: false }],
          children: [
            {
              id: 'show-clock-live',
              label: 'Live',
              path: '/show-clock/live',
              match: [{ path: '/show-clock/live', end: true }],
            },
            {
              id: 'show-clock-days',
              label: 'Daily Clocks',
              path: '/show-clock/days',
              match: [{ path: '/show-clock/days', end: true }],
            },
            {
              id: 'show-clock-templates',
              label: 'Templates',
              path: '/show-clock/templates',
              match: [{ path: '/show-clock/templates', end: true }],
            },
            {
              id: 'show-clock-audit',
              label: 'Audit',
              path: '/show-clock/audit',
              match: [{ path: '/show-clock/audit', end: true }],
            },
          ],
        },
      {
        id: 'settings',
        label: 'Settings',
        path: '/settings',
        icon: <SettingsIcon />,
        match: [
          { path: '/settings', end: true },
          { path: '/settings/*', end: false },
        ],
        children: [
          {
            id: 'settings-station',
            label: 'Station',
            path: '/settings/station',
            match: [{ path: '/settings/station', end: true }],
          },
          {
            id: 'settings-billing',
            label: 'Billing',
            path: '/settings/billing',
            match: [{ path: '/settings/billing', end: true }],
          },
          {
            id: 'settings-profile',
            label: 'Profile',
            path: '/settings/profile',
            match: [{ path: '/settings/profile', end: true }],
          },
          {
            id: 'settings-users',
            label: 'Users',
            path: '/settings/users',
            match: [{ path: '/settings/users', end: false }],
          },
          {
            id: 'settings-integrations',
            label: 'Integrations',
            path: '/settings/integrations',
            match: [
              { path: '/settings/integrations', end: true },
              { path: '/settings/integrations/*', end: false },
            ],
          },
        ],
      },
    ]

    if (showYoutubeMp3) {
      const toolsItem: NavItem = {
        id: 'tools',
        label: 'Tools',
        path: '/tools',
        icon: <BuildIcon />,
        match: [
          { path: '/tools', end: false },
          { path: '/tools/*', end: false },
        ],
        children: [
          {
            id: 'tools-youtube-mp3',
            label: 'YouTube MP3 Downloader',
            path: '/tools/youtube-mp3',
            match: [{ path: '/tools/youtube-mp3', end: true }],
          },
          {
            id: 'tools-youtube-credits',
            label: 'YouTube credits',
            path: '/tools/youtube-credits',
            match: [{ path: '/tools/youtube-credits', end: true }],
          },
        ],
      }

      const settingsIndex = baseItems.findIndex((item) => item.id === 'settings')
      if (settingsIndex >= 0) {
        baseItems.splice(settingsIndex, 0, toolsItem)
      } else {
        baseItems.push(toolsItem)
      }
    }

    if (includeSuperadmin) {
      baseItems.push({
        id: 'superadmin',
        label: 'Superadmin',
        path: '/superadmin',
        icon: <SecurityIcon />,
        match: [
          { path: '/superadmin', end: true },
          { path: '/superadmin/*', end: false },
        ],
        children: [
          {
            id: 'superadmin-general',
            label: 'General',
            path: '/superadmin/general',
            match: [{ path: '/superadmin/general', end: false }],
          },
          {
            id: 'superadmin-stations-users',
            label: 'Stations & Users',
            path: '/superadmin/stations-users',
            match: [{ path: '/superadmin/stations-users', end: false }],
            children: [
              {
                id: 'superadmin-subscriptions',
                label: 'Subscriptions',
                path: '/superadmin/stations-users/subscriptions',
                match: [{ path: '/superadmin/stations-users/subscriptions', end: false }],
              },
              {
                id: 'superadmin-grants',
                label: 'Manual grants',
                path: '/superadmin/stations-users/grants',
                match: [{ path: '/superadmin/stations-users/grants', end: false }],
              },
            ],
          },
          {
            id: 'superadmin-products',
            label: 'Products',
            path: '/superadmin/products',
            match: [{ path: '/superadmin/products', end: false }],
          },
          {
            id: 'superadmin-prep-settings',
            label: 'Prep sheet settings',
            path: '/superadmin/prep',
            match: [{ path: '/superadmin/prep', end: false }],
          },
        ],
      })
    }
    return baseItems
  }, [includeSuperadmin, showYoutubeMp3])

  const breadcrumbs = React.useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    if (segments.length === 0) {
      return [
        {
          label: breadcrumbLabels.inbox,
          to: '/inbox',
          isLast: true,
        },
      ]
    }
    return segments.map((segment, index) => {
      const to = `/${segments.slice(0, index + 1).join('/')}`
      return {
        label: breadcrumbLabels[segment] ?? segment,
        to,
        isLast: index === segments.length - 1,
      }
    })
  }, [location.pathname])

  const displayTitle = pageTitle || breadcrumbs[breadcrumbs.length - 1]?.label || ''

  const getMatchConfigs = React.useCallback(
    (match: NavMatch | NavMatch[] | undefined, fallback?: string) => {
      const entries = match ? (Array.isArray(match) ? match : [match]) : fallback ? [{ path: fallback }] : []
      return entries.map((entry) => {
        if (typeof entry === 'string') {
          const hasWildcard = entry.includes('*')
          return { path: entry, end: hasWildcard ? false : true }
        }
        const hasWildcard = entry.path.includes('*')
        return {
          path: entry.path,
          end: entry.end ?? (hasWildcard ? false : true),
        }
      })
    },
    [],
  )

  const isRouteActive = React.useCallback(
    (match: NavMatch | NavMatch[] | undefined, fallback?: string) => {
      const configs = getMatchConfigs(match, fallback)
      if (configs.length === 0) {
        return false
      }
      return configs.some((config) =>
        Boolean(matchPath({ path: config.path, end: config.end }, location.pathname)),
      )
    },
    [getMatchConfigs, location.pathname],
  )

  const isNavItemActive = React.useCallback(
    (item: NavItem): boolean => {
      const fallbackPath = item.path ?? getFirstChildPath(item)
      if (fallbackPath && isRouteActive(item.match, fallbackPath)) {
        return true
      }
      return item.children?.some((child) => isNavItemActive(child)) ?? false
    },
    [isRouteActive],
  )

  const handleNavigate = (path: string) => {
    navigate(path)
    if (!isDesktop || !navPinned) {
      closeNav()
    }
  }

  const handleDrawerTransitionEnd = React.useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget && event.propertyName === 'transform') {
        setNavAnimating(false)
      }
    },
    [setNavAnimating],
  )

  const handleUnreadClick = React.useCallback(() => {
    navigate('/inbox?filter=unread')
  }, [navigate])

  const initials = React.useMemo(() => {
    if (!user?.name) {
      return '?'
    }
    return user.name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }, [user?.name])

  const unreadTooltip = unreadTotal > 0 ? `Unread messages (${unreadTotal})` : 'Unread messages'

  const expandedDrawerContent = (
    <SidebarProvider>
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          maxHeight: '100%',
          overflow: 'hidden',
          backgroundColor: `var(--md-sys-color-surface-container-high, ${theme.palette.background.paper})`,
        })}
      >
        <Box
          component="header"
          sx={(theme) => ({
            flex: '0 0 auto',
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Toolbar
            disableGutters
            sx={{
              px: 2,
              pt: '8px',
              pb: '7px',
              alignItems: 'flex-start',
              gap: 1,
              minHeight: 'auto',
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt="RPA logo"
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 2,
                  objectFit: 'contain',
                  flexShrink: 0,
                  border: '1px solid #ffffff',
                  boxSizing: 'border-box',
                }}
              />
              <Typography variant="subtitle2" color="text.secondary">
                Presenter Assistant
              </Typography>
            </Box>
            <Tooltip title={navPinned ? 'Unpin navigation' : 'Pin navigation'}>
              <IconButton
                edge="end"
                onClick={toggleNavPinned}
                aria-label={navPinned ? 'Unpin navigation' : 'Pin navigation'}
                sx={{ ml: 0.5 }}
              >
                {navPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Box>
        <Box
          component="nav"
          aria-label="Primary navigation"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable',
          }}
        >
          <SidebarNavContent
            items={navItems}
            navSections={navSections}
            setNavSection={setNavSection}
            toggleNavSection={toggleNavSection}
            isNavItemActive={isNavItemActive}
            handleNavigate={handleNavigate}
            unreadTotal={unreadTotal}
            dangerBadgeStyle={dangerBadgeStyle}
          />
        </Box>
        <Box
          component="footer"
          sx={(theme) => ({
            flex: '0 0 auto',
            borderTop: `1px solid ${theme.palette.divider}`,
            px: 2,
            py: 2,
          })}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  Signed in as
                </Typography>
                <Typography variant="subtitle2">{user.name}</Typography>
              </Box>
              <StationSwitcher
                variant="full"
                stations={stationList}
                activeStationId={activeStationId ?? undefined}
                onSelect={handleStationSelect}
              />
            </Box>
            <Button
              variant="text"
              size="small"
              startIcon={<LogoutRoundedIcon fontSize="small" />}
              onClick={handleLogoutClick}
              disabled={logoutPending}
              sx={{ alignSelf: 'flex-start' }}
            >
              Sign out
            </Button>
          </Box>
        </Box>
      </Box>
    </SidebarProvider>
  )

  const collapsedDrawerContent = (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: '100dvh',
        maxHeight: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: `var(--md-sys-color-surface-container-high, ${theme.palette.background.paper})`,
      })}
    >
      <Box
        component="header"
        sx={(theme) => ({
          flex: '0 0 auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Toolbar
          disableGutters
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            pt: '8px',
            pb: '7px',
            gap: 1.25,
            minHeight: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              px: 1,
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="RPA logo"
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                objectFit: 'contain',
                border: '1px solid #ffffff',
                boxSizing: 'border-box',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={navOpen ? 'Close navigation' : 'Open navigation'}>
              <span>
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={toggleNav}
                  aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={navOpen ? 'true' : 'false'}
                  aria-controls="app-navigation-drawer"
                  ref={hamburgerRef}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={navPinned ? 'Unpin navigation' : 'Pin navigation'}>
              <IconButton
                color="inherit"
                size="small"
                onClick={toggleNavPinned}
                aria-label={navPinned ? 'Unpin navigation' : 'Pin navigation'}
              >
                {navPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </Box>
      <Box
        component="nav"
        aria-label="Primary navigation"
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
        }}
      >
        <List sx={{ py: 1, px: 0.5 }}>
          {navItems.map((item) => {
            const hasChildren = Boolean(item.children?.length)
            const descendantActive = hasChildren
              ? item.children?.some((child) => isNavItemActive(child)) ?? false
              : false
            const fallbackPath = item.path ?? getFirstChildPath(item)
            const parentActive = isRouteActive(item.match, fallbackPath) || descendantActive

            return (
              <Tooltip key={item.id} title={item.label} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (hasChildren) {
                      const firstChild = getFirstNavigableNode(item)
                      const firstChildPath = firstChild?.path
                      if (firstChild && firstChildPath) {
                        if (!isNavItemActive(firstChild)) {
                          handleNavigate(firstChildPath)
                          return
                        }
                      }
                      setNavSection(item.id, true)
                      openNav()
                      return
                    }
                    if (item.path) {
                      handleNavigate(item.path)
                    }
                  }}
                  selected={parentActive}
                  aria-current={parentActive ? 'page' : undefined}
                  aria-label={item.label}
                  aria-haspopup={hasChildren ? 'true' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                    py: 0.5,
                    mx: 0.5,
                    my: 0.5,
                    borderRadius: 2,
                    minHeight: 44,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 0,
                      color: 'inherit',
                      width: '100%',
                    }}
                  >
                    {item.id === 'inbox' ? (
                      <Badge
                        badgeContent={unreadTotal > 0 ? unreadTotal : null}
                        overlap="circular"
                        componentsProps={{
                          badge: {
                            className: 'BadgePill BadgePill--danger',
                            style: dangerBadgeStyle,
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )
          })}
        </List>
      </Box>
      <Box
        component="footer"
        sx={(theme) => ({
          flex: '0 0 auto',
          borderTop: `1px solid ${theme.palette.divider}`,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: theme.spacing(1),
        })}
      >
        <StationSwitcher
          variant="icon"
          stations={stationList}
          activeStationId={activeStationId ?? undefined}
          onSelect={handleStationSelect}
        />
        <Tooltip title={user.name}>
          <Avatar sx={{ width: 36, height: 36 }}>{initials}</Avatar>
        </Tooltip>
        <Tooltip title="Sign out">
          <IconButton
            size="small"
            onClick={handleLogoutClick}
            disabled={logoutPending}
            aria-label="Sign out"
          >
            <LogoutRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  const alignMap: Record<PageTitleAlign, 'start' | 'center' | 'end'> = {
    left: 'start',
    center: 'center',
    right: 'end',
  }

  return (
    <ErrorBoundary>
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
        <AppBar
          position="fixed"
          sx={{
            width: { lg: `calc(100% - ${desktopDrawerWidth}px)` },
            ml: { lg: `${desktopDrawerWidth}px` },
            backdropFilter: 'blur(12px)',
            borderTopLeftRadius: { lg: 0 },
            borderBottomLeftRadius: { lg: 0 },
          }}
        >
        <Toolbar
          ref={headerRef}
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto auto',
            gridTemplateRows: 'auto',
            alignItems: 'center',
            columnGap: theme.spacing(1.5),
            rowGap: 0,
            px: { xs: 1, sm: 1.5, md: 2.5 },
            py: { xs: 0.75, sm: 1 },
            minHeight: 64,
            '@media (max-width: 768px)': {
              gridTemplateColumns: 'auto 1fr auto auto',
              gridTemplateRows: 'auto auto',
              columnGap: theme.spacing(1),
              rowGap: theme.spacing(0.25),
              minHeight: 'auto',
              paddingTop: theme.spacing(0.5),
              paddingBottom: theme.spacing(0.5),
            },
          })}
        >
          <Box
            sx={(theme) => ({
              gridColumn: '1',
              gridRow: '1',
              display: 'flex',
              alignItems: 'center',
              justifySelf: 'start',
              '@media (max-width: 768px)': {
                gridColumn: '1',
                gridRow: '1',
              },
            })}
          >
            {!isDesktop && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleNav}
                aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={navOpen ? 'true' : 'false'}
                aria-controls="app-navigation-drawer"
                ref={hamburgerRef}
                sx={{ mr: 0.5 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
          <Box
            sx={(theme) => ({
              gridColumn: '3',
              gridRow: '1',
              justifySelf: 'center',
              minWidth: 0,
              '@media (max-width: 768px)': {
                gridColumn: '3',
                gridRow: '1',
                justifySelf: 'center',
              },
            })}
          >
            <HeaderClock />
          </Box>
          <Box
            sx={(theme) => ({
              gridColumn: '4',
              gridRow: '1',
              display: 'inline-flex',
              alignItems: 'center',
              justifySelf: 'end',
              gap: theme.spacing(1),
              '@media (max-width: 768px)': {
                gridColumn: '4',
                gridRow: '1',
                gap: theme.spacing(0.75),
              },
            })}
          >
            <WeatherButton
              stationId={activeStationId ?? null}
              stationName={weatherStationName}
              stationTimezone={weatherStationTimezone}
            />
            <MailUnreadButton
              count={unreadTotal}
              onClick={handleUnreadClick}
              tooltip={unreadTooltip}
              badgeStyle={dangerBadgeStyle}
            />
            <LiveConnectionIndicator connected={connected} lightweight={isPrepRoute} />
            <ListenerCountButton stationId={activeStationId ?? null} />
          </Box>
          <Typography
            variant="h5"
            component="div"
            sx={(theme) => ({
              gridColumn: '2',
              gridRow: '1',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 600,
              justifySelf: alignMap[pageTitleAlign],
              textAlign: pageTitleAlign,
              fontSize: { xs: '1.375rem', sm: 'inherit' },
              '@media (max-width: 768px)': {
                gridColumn: '1 / -1',
                gridRow: '2',
                justifySelf: 'center',
                textAlign: 'center',
                maxWidth: '100%',
              },
            })}
          >
            {displayTitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { xs: 0, lg: desktopDrawerWidth },
          flexShrink: { lg: 0 },
          position: 'relative',
          zIndex: 0,
        }}
        aria-label="Primary navigation"
      >
        {isDesktop ? (
          navPinned ? (
            <Drawer
              variant="permanent"
              open
              sx={{
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                  height: '100dvh',
                  maxHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              {expandedDrawerContent}
            </Drawer>
          ) : (
            <Box
              sx={{
                width: collapsedDrawerWidth,
                flexShrink: 0,
              }}
            >
              <Box
                sx={(theme) => ({
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  height: '100dvh',
                  maxHeight: '100vh',
                  width: collapsedDrawerWidth,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: theme.palette.background.paper,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  overflowY: 'auto',
                  zIndex: Math.max((theme.zIndex.appBar ?? 1100) - 2, 0),
                })}
              >
                {collapsedDrawerContent}
              </Box>
            </Box>
          )
        ) : null}
      </Box>
      {(!isDesktop || !navPinned) && portalContainer
        ? createPortal(
            <>
              <Box
                role="presentation"
                aria-hidden="true"
                onClick={closeNav}
                sx={(theme) => ({
                  position: 'fixed',
                  inset: 0,
                  backgroundColor: 'var(--surface-scrim, rgba(0,0,0,.35))',
                  opacity: navOpen ? 1 : 0,
                  pointerEvents: navOpen ? 'auto' : 'none',
                  transition: `opacity ${navTransitionDuration}ms ${theme.transitions.easing.easeOut}, visibility 0s linear ${
                    navOpen ? 0 : navTransitionDuration
                  }ms`,
                  visibility: navDrawerVisible ? 'visible' : 'hidden',
                  zIndex: (theme.zIndex.appBar ?? 1100) + 5,
                })}
              />
              <TrapFocus open={shouldShowOverlay} disableAutoFocus disableRestoreFocus>
                <Box
                  id="app-navigation-drawer"
                  ref={drawerRef}
                  role="dialog"
                  aria-modal="true"
                  tabIndex={-1}
                  onTransitionEnd={handleDrawerTransitionEnd}
                  sx={(theme) => ({
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100dvh',
                    maxHeight: '100vh',
                    width: drawerWidth,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    backgroundColor: `var(--md-sys-color-surface-container-high, ${theme.palette.background.paper})`,
                    boxShadow: 'var(--md-sys-elevation-3, 0 8px 24px rgba(0,0,0,0.2))',
                    borderTopRightRadius: 0,
                    transform: navOpen ? 'translateX(0)' : 'translateX(-100%)',
                    opacity: navOpen ? 1 : 0,
                    visibility: navDrawerVisible ? 'visible' : 'hidden',
                    transition: `transform ${navTransitionDuration}ms ${theme.transitions.easing.easeOut}, opacity ${
                      navTransitionDuration
                    }ms ${theme.transitions.easing.easeOut}, visibility 0s linear ${navOpen ? 0 : navTransitionDuration}ms`,
                    willChange: 'transform',
                    pointerEvents: navOpen ? 'auto' : 'none',
                    zIndex: (theme.zIndex.appBar ?? 1100) + 10,
                  })}
                >
                  {expandedDrawerContent}
                </Box>
              </TrapFocus>
            </>,
            portalContainer,
          )
        : null}
      <PageTitleProvider value={pageTitleContextValue}>
        <Box
          component="main"
          className="rpa-page"
          sx={{
            flexGrow: 1,
            width: { lg: `calc(100% - ${desktopDrawerWidth}px)` },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              pt: showBreadcrumbs ? { xs: 2, md: 3 } : 0,
              px: showBreadcrumbs ? { xs: 2, md: 3 } : 0,
              pb: showBreadcrumbs ? { xs: 2, md: 3 } : 0,
            }}
          >
            {showBreadcrumbs ? (
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs.map((crumb) =>
                  crumb.isLast ? (
                    <Typography key={crumb.to} color="text.primary">
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Link
                      key={crumb.to}
                      color="inherit"
                      underline="hover"
                      component="button"
                      onClick={() => handleNavigate(crumb.to)}
                    >
                      {crumb.label}
                    </Link>
                  ),
                )}
              </Breadcrumbs>
            ) : null}
            <Outlet
              context={{
                me,
                includeSuperadmin,
                activeStation,
                setActiveStation: setStation,
                updateMe: onUpdateMe,
              }}
            />
          </Box>
        </Box>
      </PageTitleProvider>
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutClose}
        aria-labelledby="rpa-logout-title"
      >
        <DialogTitle id="rpa-logout-title">Sign out?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You will need to sign in again to access the app.
          </Typography>
          {logoutError ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {logoutError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutClose} disabled={logoutPending}>
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" disabled={logoutPending}>
            {logoutPending ? 'Signing out...' : 'Sign out'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </ErrorBoundary>
  )
}

export default AppShell



